<template>
  <el-dialog
    :title="$t('editReservationDialog.title')"
    :visible="true"
    :close-on-click-modal="false"
    :width="width"
    @close="$emit('close')"
  >
    <BaseElForm
      ref="form"
      v-loading="!loaded"
      :model="formData"
      :rules="mergedFormRules"
      label-position="top"
    >
      <!-- CBGISSUE-243：一次有兩個以上欄位失效時，最上方補一條摘要 -->
      <p v-if="staleSummary" class="mb-2 text-gray-80 text-sub">{{ staleSummary }}</p>
      <BaseElFormItem v-if="!formData.isMember" :label="isPubApt ? $t('orderDetailBlock.visitor.name.title') : $t('orderDetailBlock.user.name.title')" prop="userName">
        <BaseElInput v-model="formData.userName" :placeholder="$t('branchSetting.dialog.branchName.placeholder')" :disabled="isPubApt" />
      </BaseElFormItem>
      <BaseElFormItem v-if="!formData.isMember" :label="isPubApt ? $t('editReservationDialog.visitor.phone.title') : $t('editReservationDialog.user.phone.title')" prop="userPhone">
        <BaseElInput v-model="formData.userPhone" :placeholder="$t('editReservationDialog.user.phone.placeholder')" :disabled="isPubApt" />
      </BaseElFormItem>
      <BaseElFormItem v-if="checkResource" :label="$t('editReservationDialog.type.title')" prop="type">
        <BaseElSelect
          v-model="formData.type"
          value-key="id"
          :placeholder="$t('common.select.placeholder')"
          :no-data-text="$t('common.table.empty.text')"
          :disabled="disableFields"
          @change="serviceTypeChange()"
        >
          <BaseElSelectOption
            v-for="item in serviceTypeConfig"
            :key="item.value"
            :label="$t(item.label)"
            :value="item.value"
          />
        </BaseElSelect>
      </BaseElFormItem>
      <BaseElFormItem v-if="showHumanService" :label="$t('orderDetailBlock.serviceUnit.title')" prop="serviceUnit">
        <ServicesUnitSelect
          :model.sync="formData.serviceUnit"
          :disabled="disableFields"
          :staleReason="staleFields.serviceUnit"
          @change="serviceUnitChange(formData.serviceUnit), (formData.totalTime = 0)"
        />
        <p v-if="staleFieldHints.serviceUnit" class="text-gray-80 text-sub">{{ staleFieldHints.serviceUnit }}</p>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderDetailBlock.serviceName.title')" prop="service">
        <BaseElSelect
          v-model="formData.service"
          value-key="id"
          filterable
          clearable
          :disabled="disableFields"
          :no-data-text="staleEmptyText.service"
          @change="serviceChange"
          @clear="serviceChange"
        >
          <BaseElSelectOption
            v-for="service in filterServiceOptions"
            :key="service.id"
            :value="service"
            :label="staleOptionLabel(service.name, optionStaleReason(service))"
            :disabled="Boolean(optionStaleReason(service))"
          />
        </BaseElSelect>
        <p v-if="staleFieldHints.service" class="text-gray-80 text-sub">{{ staleFieldHints.service }}</p>
        <p v-if="classTicketUnavailable && data.isUseClassTicket" class="text-gray-80 text-sub">
          {{ $t('editReservationDialog.classTicketUnavailable.title') }}
        </p>
      </BaseElFormItem>
      <BaseElFormItem v-if="enableSubService" :label="$t('orderDetailBlock.subServiceName.title')" prop="subService">
        <BaseElSelect
          v-model="formData.subService"
          value-key="id"
          :disabled="disableFields"
          :no-data-text="staleEmptyText.subService"
          @change="subServiceChange"
        >
          <BaseElSelectOption
            v-for="service in subServiceList"
            :key="service.id"
            :label="staleOptionLabel(service.name, optionStaleReason(service))"
            :disabled="Boolean(optionStaleReason(service))"
            :value="service"
          />
        </BaseElSelect>
        <p v-if="staleFieldHints.subService" class="text-gray-80 text-sub">{{ staleFieldHints.subService }}</p>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderDetailBlock.attachService.title')" prop="attachServices">
        <AttachServiceSelect
          :model.sync="formData.attachServices"
          :services="formData.service"
          multiple
          :disabled="disableFields"
          @change="attachServiceChange"
        />
      </BaseElFormItem>
      <BaseElFormItem v-if="showResourceService && checkResource" :label="$t('orderDetailBlock.resource.title')" prop="resourceItem">
        <ResourceUnitSelect
          data-cy="unit-binding-select"
          :model.sync="formData.resourceItem"
          :data="resourceItemOptions"
          :showAll="true"
          :disabled="disableFields"
          :noDataText="staleEmptyText.resourceItem"
        />
        <p v-if="staleFieldHints.resourceItem" class="text-gray-80 text-sub">{{ staleFieldHints.resourceItem }}</p>
      </BaseElFormItem>
      <BaseElFormItem v-if="showControl.formItem.bookingTime" :label="$t('editReservationDialog.totalTime.title')" prop="totalTime">
        <BaseElSelect
          v-model="formData.totalTime"
          :placeholder="$t('common.select.placeholder')"
          :disabled="disableFields"
          @change="totalTimeChange"
        >
          <BaseElSelectOption
            v-for="(item, index) in displayTotalTimeOptions"
            :key="index"
            :label="timeOptionLabel(item, 'totalTime')"
            :value="item"
          />
        </BaseElSelect>
        <p v-if="staleFieldHints.totalTime" class="text-gray-80 text-sub">{{ staleFieldHints.totalTime }}</p>
      </BaseElFormItem>
      <BaseElFormItem v-if="showControl.formItem.resourceBookingTime && checkResource" :label="$t('editReservationDialog.resourceTotalTime.title')" prop="resourceTotalTime">
        <template slot="label">
          <div class="flex items-center" style="gap: 8px">
            <p>{{ $t('editReservationDialog.resourceTotalTime.title') }}</p>
            <el-tooltip v-show="showControl.tipInfo.resourceBookingTime" placement="right">
              <div slot="content">
                {{ $t('editReservationDialog.resourceTotalTime.tooltip') }}
              </div>
              <span class="material-icons">help_outline</span>
            </el-tooltip>
          </div>
        </template>
        <div
          v-if="formData.type === 'humanAndResourceService' && !formData.totalTime"
          class="text-gray-100"
        >
          {{ $t('editReservationDialog.selectTotalTimeFirst.title') }}
        </div>
        <BaseElSelect
          v-else
          v-model="formData.resourceTotalTime"
          data-cy="total-time-select"
          :placeholder="$t('common.select.placeholder')"
          :disabled="disableFields"
        >
          <BaseElSelectOption
            v-for="(item, index) in displayResourceTotalTimeOptions"
            :key="index"
            :label="timeOptionLabel(item, 'resourceTotalTime')"
            :value="item"
          />
        </BaseElSelect>
        <p v-if="staleFieldHints.resourceTotalTime" class="text-gray-80 text-sub">{{ staleFieldHints.resourceTotalTime }}</p>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('editReservationDialog.isOverTime.title')">
        <BaseElRadioGroup
          v-model="formData.isOverTime"
          :disabled="disableFields"
          @change=";(formData.time = ''), (formData.date = '')"
        >
          <BaseElRadio :label="true">{{ $t('common.yes.text') }}</BaseElRadio>
          <BaseElRadio :label="false">{{ $t('common.no.text') }}</BaseElRadio>
        </BaseElRadioGroup>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('editReservationDialog.date.title')" prop="date">
        <div v-if="!formData.type || formData.service === ''">
          {{ $t('editReservationDialog.selectTypeFirst.title') }}
        </div>
        <el-date-picker
          v-if="formData.service !== ''"
          ref="picker"
          v-model="formData.date"
          editable
          :picker-options="pickerOptions"
          type="date"
          :placeholder="$t('editReservationDialog.date.placeholder')"
          :disabled="disableFields"
          @focus="getDate"
          @change="dateChange"
        />
        <!-- CBGISSUE-243：綁定已變更導致算不出可預約時段時，說明原因（前端可判斷的情況） -->
        <p v-if="staleFieldHints.dateTime" class="text-gray-80 text-sub">{{ staleFieldHints.dateTime }}</p>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('editReservationDialog.time.title')" prop="time">
        <div v-if="!formData.date">{{ $t('editReservationDialog.selectDateFirst.title') }}</div>
        <AvailableTimeSelect
          v-if="formData.date && !formData.isOverTime"
          :model.sync="formData.time"
          :timesList="availableTimes"
          :disabled="disableFields"
        />
        <BaseTimePicker
          v-if="formData.date && formData.isOverTime"
          :date="formData.date"
          :value.sync="formData.time"
          :disabled="disableFields"
          :picker-options="{
            selectableRange: '00:00:00 - 23:59:59'
          }"
          :placeholder="$t('common.select.placeholder')"
          :clearable="false"
        />
      </BaseElFormItem>
      <!-- <BaseElFormItem :label="$t('editReservationDialog.comment.title')" prop="comment">
        <BaseElInput v-model="formData.comment" :placeholder="$t('editReservationDialog.comment.placeholder')" />
      </BaseElFormItem> -->
      <!-- <BaseElFormItem :label="$t('editReservationDialog.remark.title')" prop="remark">
        <BaseElInput v-model="formData.remark" :placeholder="$t('editReservationDialog.remark.placeholder')" />
      </BaseElFormItem> -->
    </BaseElForm>

    <div slot="footer">
      <BaseElButton plain @click="$emit('close')">{{ $t('common.button.cancel.text') }}</BaseElButton>
      <BaseElButton
        ref="createBtn"
        v-loading="loading"
        type="primary"
        @click="updateInfoAndTime"
      >
        {{ $t('common.button.confirm.text') }}
      </BaseElButton>
    </div>
  </el-dialog>
</template>

<script>
import axios from 'axios'
import { defineComponent, ref, reactive, computed } from 'vue'
import { mapGetters, mapActions } from 'vuex'
// import UnitServicesSelect from '@/components/Select/UnitServicesSelect.vue'
import ServicesUnitSelect from '@/components/Select/ServicesUnitSelect.vue'
import AttachServiceSelect from '@/components/Select/AttachServiceSelect.vue'
import ResourceUnitSelect from '@/components/Select/ResourceUnitSelect.vue'
import { UpdateReservationTimeServices, UpdateReservationInfo } from '@/api/reservation'
import AvailableTimeSelect from '@/components/Reservation/AvailableTimeSelect/AvailableTimeSelect.vue'
import dayjs from '@/lib/dayjs'
import { get, filter, map, find, includes, compact, isEmpty, forEach } from 'lodash'
import debounce from 'lodash.debounce'
import { noEmptyRules, phoneRules } from '@/validation'
import formUtils from '@/utils/form'
import { generateTimes, generateNumbers, sortOrder } from '@/utils/helper'
import { getMonth, thisMonth, isInvalidDate } from '@/utils/date'
import { serviceTypeConfig } from '@/config/reservation'
import { checkUserFeature } from '@/store/modules/permission'
import { FindService, GetService } from '@/api/service'
import { GetResourceItem } from '@/api/resource'
import { FindServiceUnit } from '@/api/serviceUnit'
import BaseTimePicker from '@/components/BaseTimePicker.vue'
import { useFetch } from '@/use/fetch'
import { apiFormatAdaptor } from '@/utils/api'
import useDirty from '@/use/useDirty'
import { useShop } from '@/use/shop'
import { availabilityEndpoints, clearAvailabilityQueries, useAvailabilityQuery } from '@/use/useAvailabilityQuery'
import { useQueryClient } from '@tanstack/vue-query'
import { createAvailabilityPickerPanelBinding } from '@/use/useAvailabilityPickerPanel'
import {
  optionStaleReason,
  staleBlockedMessage,
  staleEmptyHint,
  staleFieldHint,
  staleOptionLabel,
  staleReasonConfig,
  staleSummaryText,
  virtualUnitIds,
  withPreservedOption,
} from '@/use/useReservationStaleField'

export default defineComponent({
  name: 'EditReservationDialog',
  components: {
    // UnitServicesSelect,
    ServicesUnitSelect,
    AttachServiceSelect,
    AvailableTimeSelect,
    ResourceUnitSelect,
    BaseTimePicker,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    show: Boolean,
    width: String,
    member: Object,
    usingClassTickets: { type: Array, default: () => ([]) },
    isPubApt: Boolean,
    disableFields: Boolean,
  },

  setup (props, { emit }) {
    const { fetchAll } = useFetch()
    const { shopId } = useShop()
    const classTicketUnavailable = ref(false)
    const loaded = ref(false)
    const loading = ref(false)
    const formData = reactive({
      isMember: false,
      member: {
        UserInfo: {
          name: '',
          phone: '',
        },
      },
      isOverTime: false,
      serviceUnit: '',
      service: '',
      subService: null,
      attachServices: [],
      date: '',
      time: '',
      totalTime: 0,
      comment: '',
      remark: '',
      serviceTime: 0,
      attachServiceTimes: 0,
      userName: '',
      userPhone: '',
      type: 'humanService',
      resourceItem: {},
      resourceTotalTime: 0,
    })
    const { dirtyFields, startWatch } = useDirty(formData)
    const queryClient = useQueryClient()
    const availabilityPayload = ref(null)
    const { query, cancel, getCachedData } = useAvailabilityQuery({
      endpoint: availabilityEndpoints.times,
      payload: availabilityPayload,
    })
    const availabilityPickerPanelBinding = createAvailabilityPickerPanelBinding()

    const checkClassTicketStillCanUse = (service, classTicketId) => {
      const serviceClassTickets = map(service.ClassTickets, 'id')
      return serviceClassTickets.includes(classTicketId)
    }
    const genNums = ref([])
    const getResourceNums = ref([])

    const formRules = computed(() => {
      const rules = {
        // member: noEmptyRules(),
        // userName: noEmptyRules(),
        type: noEmptyRules,
        userPhone: [noEmptyRules()],
        name: noEmptyRules(),
        serviceUnit: formData.type === 'resourceService' ? null : noEmptyRules(),
        service: noEmptyRules(),
        subService: noEmptyRules(),
        date: noEmptyRules(),
        time: noEmptyRules(),
        resourceItem: noEmptyRules(),
      }

      if (dirtyFields('userPhone')) {
        rules.userPhone = [noEmptyRules(), phoneRules()]
      }

      return rules
    })

    const showControl = computed(() => {
      const controls = {
        formItem: {
          bookingTime: true,
          resourceBookingTime: false,
        },
        tipInfo: {
          resourceBookingTime: false,
        },
      }

      if (formData.type === 'resourceService') {
        controls.formItem.resourceBookingTime = true
        controls.formItem.bookingTime = false
      } else if (formData.type === 'humanAndResourceService') {
        controls.formItem.resourceBookingTime = true
        controls.formItem.bookingTime = true
      }

      if (formData.type === 'humanAndResourceService') {
        controls.tipInfo.resourceBookingTime = true
      }

      return controls
    })

    const updateOrderInfo = async () => {
      const payload = {
        shopId: shopId.value,
        id: get(props, 'data.id'),
        userName: formData.userName,
        userPhone: formData.userPhone,
      }

      if (!dirtyFields('userPhone')) delete payload.userPhone

      const [, err] = await UpdateReservationInfo(payload)
      if (err) {
        window.$message.error(err)
        loading.value = false
        return false
      }
    }

    // TODO 檢查目前堂票是否適用新服務
    return {
      fetchAll,
      loaded,
      formData,
      showControl,
      classTicketUnavailable,
      checkClassTicketStillCanUse,
      genNums,
      getResourceNums,
      formRules,
      loading,
      updateOrderInfo,
      startWatch,
      availabilityPayload,
      availabilityData: query.data,
      availabilityError: query.error,
      cancelAvailability: cancel,
      getCachedAvailability: getCachedData,
      availabilityPickerPanelBinding,
      queryClient,
    }
  },

  data () {
    return {
      availableTimes: [],
      availableTimesSource: [],
      availableDate: [],
      pickerOptions: {
        disabledDate: (date) => {
          const cur = new Date(date)
          const year = get(this.$refs, 'picker.picker.year')
          const month = get(this.$refs, 'picker.picker.month')
          // 如果 date 的 year 與 year 不同，return false
          if (cur.getFullYear() !== year) return false
          if (cur.getMonth() !== month) return false
          if (this.formData.isOverTime) return false
          const d = dayjs(date).format('YYYY/MM/DD')
          // 過往日期不可選（加班模式除外，上方已豁免）
          if (dayjs(date).isBefore(dayjs(), 'day')) return true
          return !this.availableDate.find((item) => item === d)
        },
      },
      serviceTypeConfig,
      serviceOptions: [],
      mainServiceData: null,
      allResourceItems: [],
      // CBGISSUE-243：原服務項目已被刪除（詳情 API 回 404），改用預約當下的歷史快照
      serviceRemoved: false,
    }
  },
  computed: {
    ...mapGetters([
      'shop',
      'shopTimeUnit',
      'userFeatures',
      'userPlanFeature']),
    picker () {
      return this.$refs.picker
    },
    //= > 服務時數 + 附加服務時數
    totalTime () {
      const form = this.formData
      if (!form.service) return 0
      let base = 0

      if (!form.subService) base += form.service.bookingTime
      if (form.subService) base += form.subService.bookingTime

      let attach = 0
      if (form.attachServices) {
        form.attachServices.forEach((item) => {
          attach += item.bookingTime
        })
      }

      return base + attach
    },
    totalResourceTime () {
      const form = this.formData
      if (!form.service) return 0
      let base = 0

      if (!form.subService) base += form.service.bookingTime
      if (form.subService) base += form.subService.bookingTime

      let attach = 0
      if (form.attachServices) {
        // 如果為人員和設備服務
        const attachServiceSettings = get(form.service, 'attachServiceSettings', [])
        form.attachServices.forEach((item) => {
          const enableResourceItemBooking = get(find(attachServiceSettings, {
            appointmentServiceAttachId: get(item, 'id'),
          }), 'enableResourceItemBooking')
          if (enableResourceItemBooking) attach += item.bookingTime
        })
      }

      return base + attach
    },
    ohbotBookingId () {
      const ids = []
      const OhbookBookingId = get(this.data, 'OhbookBookingId')
      const ResourceItemOhbookBookingId = get(this.data, 'ResourceItemOhbookBookingId')
      if (OhbookBookingId) ids.push(OhbookBookingId)
      if (ResourceItemOhbookBookingId) ids.push(ResourceItemOhbookBookingId)
      return ids
    },
    attachServicesList () {
      return map(this.formData.attachServices, 'id')
    },
    baseSubServiceOptions () {
      if (!this.mainServiceData) return []
      else if (get(this.formData, 'type') === 'resourceService') return get(this.mainServiceData, 'AppointmentSubServices')
      else if (get(this.formDatam, 'serviceUnit') && includes(['notSpecify', 'all'], get(this.formData, 'serviceUnit.id'))) return get(this.mainServiceData, 'AppointmentSubServices')
      else if (get(this.formData, 'serviceUnit')) return get(this.mainServiceData, 'AppointmentSubServices').filter(s => find(s.AppointmentUnits, { id: get(this.formData, 'serviceUnit.id') }) || isEmpty(s.AppointmentUnits))
      else return []
    },
    // ?? 顯示的服務子項目選項（補回已失效但仍被選取的歷史值）
    subServiceList () {
      return withPreservedOption(this.baseSubServiceOptions, this.formData.subService, this.staleFields.subService)
    },
    enableSubService () {
      return get(this.formData.service, 'enableSubService')
    },
    checkResource () {
      return this.checkAction('planModule.appointmentResource.enable')
    },
    // showServiceTypeConfig () {
    //   const omitList = []
    //   if (!this.checkAction('admin.resourceItem.page')) omitList.push('resourceService', 'humanAndResourceService')
    //   return omit(this.serviceTypeConfig, omitList)
    // },
    showHumanService () {
      return this.formData.type === 'humanService' || this.formData.type === 'humanAndResourceService'
    },
    showHumanAndResourceService () {
      return this.formData.type === 'humanAndResourceService'
    },
    baseServiceOptions () {
      let serviceOptionList = []
      if (this.checkResource) {
        if (this.formData.type === 'resourceService') {
          serviceOptionList = filter(this.serviceOptions, (i) => !i.enableAppointmentUnit && i.enableResourceItem)
        } else if (this.formData.type === 'humanService') {
          serviceOptionList = filter(this.serviceOptions, (i) => i.enableAppointmentUnit && !i.enableResourceItem)
        } else if (this.formData.type === 'humanAndResourceService') {
          serviceOptionList = filter(this.serviceOptions, (i) => i.enableAppointmentUnit && i.enableResourceItem)
        } else {
          serviceOptionList = this.serviceOptions
        }
      } else {
        serviceOptionList = filter(this.serviceOptions, (i) => i.enableAppointmentUnit && !i.enableResourceItem)
      }
      return serviceOptionList
    },
    // ?? 顯示的服務項目選項（補回已失效但仍被選取的歷史值）
    filterServiceOptions () {
      return withPreservedOption(this.baseServiceOptions, this.formData.service, this.staleFields.service)
    },
    // ?? 服務項目本身是否還符合目前的服務性質
    serviceTypeMatched () {
      const service = this.mainServiceData
      if (!service) return true
      if (this.formData.type === 'resourceService') return !service.enableAppointmentUnit && service.enableResourceItem
      if (this.formData.type === 'humanAndResourceService') return Boolean(service.enableAppointmentUnit && service.enableResourceItem)
      return Boolean(service.enableAppointmentUnit && !service.enableResourceItem)
    },
    // ?? 可選的服務設備（對齊新增預約：已停用的設備不可選）
    baseResourceItemOptions () {
      // 與新增預約的 baseResourceOptions 用同一組條件，避免兩個入口對「已停用」判斷不一致
      return filter(get(this.mainServiceData, 'ResourceItems', []), { enabled: true, isRemove: false })
    },
    resourceItemOptions () {
      return withPreservedOption(this.baseResourceItemOptions, this.formData.resourceItem, this.staleFields.resourceItem)
    },
    displayTotalTimeOptions () {
      return this.withPreservedTimeOption(this.genNums, this.formData.totalTime)
    },
    displayResourceTotalTimeOptions () {
      return this.withPreservedTimeOption(this.getResourceNums, this.formData.resourceTotalTime)
    },
    /**
     * CBGISSUE-243：預約成立後店家改了設定（刪除 / 解除綁定 / 改服務性質 / 停用），
     * 開啟編輯預約時原本這些欄位會直接變空白、下拉只剩「無資料」。
     * 這裡統一判定各欄位的失效原因，供選項標記、欄位說明、摘要與送出前阻擋使用。
     */
    staleFields () {
      const result = {
        serviceUnit: null,
        service: null,
        subService: null,
        resourceItem: null,
        totalTime: null,
        resourceTotalTime: null,
        dateTime: null,
      }
      if (!this.loaded) return result

      const serviceUnitId = get(this.formData, 'serviceUnit.id')
      const hasRealServiceUnit = Boolean(serviceUnitId) && !includes(virtualUnitIds, serviceUnitId)

      // 服務人員：已被刪除
      // 預約詳情帶的是關聯資料本身（含 isRemove），可選清單只會回未刪除的人員；
      // 這裡以 isRemove 判定，避免服務人員清單被權限縮限的角色被誤判成已刪除
      if (hasRealServiceUnit && get(this.formData, 'serviceUnit.isRemove')) {
        result.serviceUnit = staleReasonConfig.removed
      }

      // 服務項目：已刪除 / 服務性質被改過 / 已與所選服務人員解除綁定
      const serviceId = get(this.formData, 'service.id')
      if (this.serviceRemoved) result.service = staleReasonConfig.removed
      else if (serviceId && !find(this.baseServiceOptions, { id: serviceId })) {
        if (!this.serviceTypeMatched) result.service = staleReasonConfig.typeChanged
        else if (hasRealServiceUnit && !result.serviceUnit) result.service = staleReasonConfig.unbound
      }

      // 服務子項目：已刪除或已不再綁定所選服務人員
      const subServiceId = get(this.formData, 'subService.id')
      if (subServiceId && !result.service && !find(this.baseSubServiceOptions, { id: subServiceId })) {
        result.subService = get(this.formData, 'subService.isRemove')
          ? staleReasonConfig.removed
          : staleReasonConfig.unbound
      }

      // 服務設備：已刪除 / 已停用 / 已與服務項目解除綁定
      const resourceItemId = get(this.formData, 'resourceItem.id')
      if (resourceItemId && !result.service && !find(this.baseResourceItemOptions, { id: resourceItemId })) {
        // 服務項目詳情的 ResourceItems 已濾掉已刪除的設備，所以找不到就是已刪除或已解除綁定
        const bound = find(get(this.mainServiceData, 'ResourceItems', []), { id: resourceItemId })
        if (!bound) result.resourceItem = staleReasonConfig.unboundFromService
        else result.resourceItem = staleReasonConfig.disabled
      }

      // 預估服務時數 / 設備使用時數：店家調整過時間單位或服務時長，原本的時數已不在選項內
      if (this.formData.totalTime && !includes(this.genNums, this.formData.totalTime)) {
        result.totalTime = staleReasonConfig.outOfRange
      }
      if (this.formData.resourceTotalTime && !includes(this.getResourceNums, this.formData.resourceTotalTime)) {
        result.resourceTotalTime = staleReasonConfig.outOfRange
      }

      // 預約日期 / 時段：前端已能判斷的情況（所選服務人員不在服務項目的綁定名單內）
      if (!result.service && !result.serviceUnit && hasRealServiceUnit) {
        const boundUnits = get(this.mainServiceData, 'AppointmentUnits')
        if (boundUnits && boundUnits.length && !find(boundUnits, { id: serviceUnitId })) {
          result.dateTime = staleReasonConfig.unbound
        }
      }

      return result
    },
    // ?? CBGISSUE-243：欄位上保留的失效歷史值不可直接送出，
    // 停在該欄位顯示同一句原因（只有真的失效時才掛規則，正常流程與原本相同）
    mergedFormRules () {
      const staleProps = filter(
        ['serviceUnit', 'service', 'subService', 'resourceItem'],
        (field) => this.staleFieldHints[field] && this.formRules[field],
      )
      // 沒有欄位失效時直接沿用原本的 rules（含物件參照），
      // 避免 el-form 的 validateOnRuleChange 觸發多餘的整表重驗
      if (!staleProps.length) return this.formRules

      const rules = { ...this.formRules }
      forEach(staleProps, (field) => {
        const base = Array.isArray(rules[field]) ? rules[field] : [rules[field]]
        rules[field] = [
          ...base,
          {
            trigger: ['change', 'blur'],
            validator: (rule, value, callback) => callback(new Error(staleBlockedMessage())),
          },
        ]
      })
      return rules
    },
    // ?? 失效欄位的下方說明
    staleFieldHints () {
      const stale = this.staleFields
      const unitName = get(this.formData, 'serviceUnit.name')
      const serviceName = get(this.formData, 'service.name')
      return {
        serviceUnit: staleFieldHint('serviceUnit', stale.serviceUnit, { name: unitName }),
        service: staleFieldHint('service', stale.service, { name: serviceName, unitName }),
        subService: staleFieldHint('subService', stale.subService, { name: get(this.formData, 'subService.name'), unitName }),
        resourceItem: staleFieldHint('resourceItem', stale.resourceItem, { name: get(this.formData, 'resourceItem.name'), serviceName }),
        totalTime: staleFieldHint('bookingTime', stale.totalTime, { name: this.formData.totalTime }),
        resourceTotalTime: staleFieldHint('resourceBookingTime', stale.resourceTotalTime, { name: this.formData.resourceTotalTime }),
        dateTime: staleFieldHint('dateTime', stale.dateTime, { name: serviceName, unitName }),
      }
    },
    // ?? 兩個以上欄位失效時的摘要
    staleSummary () {
      const stale = this.staleFields
      const labels = compact([
        stale.serviceUnit && this.$t('orderDetailBlock.serviceUnit.title'),
        stale.service && this.$t('orderDetailBlock.serviceName.title'),
        stale.subService && this.$t('orderDetailBlock.subServiceName.title'),
        stale.resourceItem && this.$t('orderDetailBlock.resource.title'),
      ])
      if (labels.length < 2) return ''
      return staleSummaryText(labels)
    },
    // ?? 下拉清單真的是空的時候，把「無資料」換成有原因的句子
    staleEmptyText () {
      const fallback = this.$t('common.table.empty.text')
      const text = { service: fallback, subService: fallback, resourceItem: fallback }
      const serviceUnitId = get(this.formData, 'serviceUnit.id')
      const serviceName = get(this.formData, 'service.name')

      if (isEmpty(this.baseServiceOptions)) {
        if (serviceUnitId && !includes(virtualUnitIds, serviceUnitId) && isEmpty(this.serviceOptions) && !this.staleFields.serviceUnit) {
          text.service = staleEmptyHint('service.byServiceUnit', { name: get(this.formData, 'serviceUnit.name') })
        } else {
          text.service = staleEmptyHint('service.byServiceType')
        }
      }
      if (serviceName && isEmpty(this.baseSubServiceOptions)) {
        text.subService = staleEmptyHint('subService', { name: serviceName })
      }
      if (serviceName && isEmpty(this.baseResourceItemOptions)) {
        text.resourceItem = staleEmptyHint('resourceItem', { name: serviceName })
      }
      return text
    },
    showResourceService () {
      return includes(['resourceService', 'humanAndResourceService'], this.formData.type)
    },
  },

  watch: {
    'formData.service': function () {
      this.resetAvailability()
      this.formData.date = ''
    },

    'formData.serviceUnit': function () {
      this.resetAvailability()
      this.formData.date = ''
    },
    'formData.type': function (data) {
      this.resetAvailability()
      if (data === 'resourceService') this.formRules.serviceUnit = null
      else this.formRules.serviceUnit = noEmptyRules()
    },
    availabilityData (res) {
      this.applyAvailabilityData(res)
    },
    availabilityError (error) {
      if (error && !axios.isCancel(error)) this.$message.error(error.message)
    },
  },

  beforeDestroy () {
    this.availabilityPickerPanelBinding.unbind()
  },

  async mounted () {
    await this.getShopReservationConfig({ shopId: this.shop })
    await this.findService(this.data.AppointmentService.id)
    if (this.checkAction('admin.resourceItem.find')) await this.getResourceItem()
    await this.syncData()
    this.updateTimeOptions()
    // CBGISSUE-243：服務項目清單要載完才能判斷欄位是否失效，否則會先閃一次誤判
    if (this.formData.serviceUnit) await this.findServiceUnit(this.formData.serviceUnit.id)
    else await this.getService()
    this.startWatch()
    this.loaded = true
  },

  methods: {
    async syncData () {
      const AppointmentVisitorPreOrder = get(this.data, 'AppointmentOrder.AppointmentVisitorPreOrder')
      this.formData.userName = this.isPubApt ? get(AppointmentVisitorPreOrder, 'profile.lastName') + get(AppointmentVisitorPreOrder, 'profile.firstName') : this.data.userName
      this.formData.userPhone = this.isPubApt ? `${get(AppointmentVisitorPreOrder, 'profile.phoneCountryCode')} ${get(AppointmentVisitorPreOrder, 'profile.phoneNumber')}` : this.data.userPhone

      this.formData.serviceUnit = this.data.AppointmentUnit
      // CBGISSUE-243：設備已被刪除或不在清單內時，保留預約當下的歷史快照，
      // 讓欄位標記原因而不是無聲變空白
      this.formData.resourceItem = this.allResourceItems.find(item => item.id === this.data.ResourceItemId) ||
        get(this.data, 'ResourceItem') ||
        undefined
      this.formData.attachServices = this.data.AppointmentServiceAttaches
      this.formData.subService = this.data.AppointmentSubService

      this.formData.totalTime = this.data.totalBookingTime

      this.formData.resourceTotalTime = this.data.resourceItemTotalBookingTime
      this.formData.isOverTime = this.data.isOvertime
      setTimeout(() => {
        this.formData.date = this.data.start
        this.formData.time = this.data.start
        const start = dayjs(this.data.start).startOf('d')
        const end = dayjs(this.data.start).add(1, 'd').startOf('d')
        this.getAvailableTimes({ start: dayjs(start), end: dayjs(end) })
        // formRules 重算觸發 validateOnRuleChange 時 date/time 尚未設定而殘留的
        // time 驗證錯誤；值帶入後 el-select 不會自動 dispatch el.form.change，
        // 需手動重新驗證清除
        this.$nextTick(() => formUtils.checkFormField(this.$refs.form, 'time'))
      }, 0)
      if (this.data.ResourceItem) {
        this.formData.type = 'resourceService'
        this.formRules.serviceUnit = null
      }
      if (this.data.AppointmentUnit) {
        this.formData.type = 'humanService'
      }
      if (this.data.ResourceItem && this.data.AppointmentUnit) {
        this.formData.type = 'humanAndResourceService'
      }
      const start = dayjs(this.data.start).startOf('d')
      const end = dayjs(this.data.start).add(1, 'd').startOf('d')
      this.getAvailableTimes({
        start: dayjs(start),
        end: dayjs(end),
      })
    },
    async getResourceItem  () {
      const payload = {
        shopId: this.shop,
        start: 0,
        limit: 100,
      }

      await this.fetchAll(GetResourceItem, payload, (res) => {
        this.allResourceItems = res
      })
    },
    async getService (query = null) {
      try {
        this.serviceOptions = []
        setTimeout(async () => {
          await this.fetchAll(apiFormatAdaptor(GetService), {
            shopId: this.shop,
            name: (query) || undefined,
            limit: 500,
          }, (res) => {
            this.serviceOptions = res
          })
        }, 200)
      } catch (error) {
        console.log(error)
      }
    },
    checkAction (action) {
      return checkUserFeature(this.userPlanFeature, this.userFeatures, action)
    },
    getNumsByValue (value) {
      return generateNumbers(0, value + this.shopTimeUnit, this.shopTimeUnit)
    },
    staleOptionLabel,
    optionStaleReason,
    // CBGISSUE-243：時間單位或服務時長被調整後原本的時數會不在選項內導致欄位變空白，
    // 這裡把原值補回選項並加註原因；時數本身仍是合法的送出值，因此保留可選取
    withPreservedTimeOption (options, value) {
      if (!value || includes(options, value)) return options || []
      return [...(options || []), value].sort((a, b) => a - b)
    },
    timeOptionLabel (value, field) {
      const reason = get(this.staleFields, field)
      if (reason && value === get(this.formData, field)) return staleOptionLabel(value, reason)
      return value
    },
    debounce,
    ...mapActions('shop', [
      'getShopReservationConfig',
    ]),
    resetAvailability () {
      this.cancelAvailability()
      this.availabilityPayload = null
      this.availableDate = []
      this.availableTimes = []
      this.availableTimesSource = []
    },
    applyAvailabilityData (res) {
      if (!res || !res.times) return

      this.availableDate = Array.from(new Set(res.times.map(item => dayjs(item).format('YYYY/MM/DD'))))
      this.availableTimesSource = res.times
      this.updateAvailableTimes(this.formData.date)
    },
    getAvailableTimes ({ start, end }) {
      let appointmentUnitIds = get(this.formData, 'serviceUnit.id') ? [get(this.formData, 'serviceUnit.id')] : null
      // const useNotSpecify = get(this.configData, 'useNotSpecify')
      if (appointmentUnitIds) {
        if (appointmentUnitIds.includes('all')) appointmentUnitIds = null
        else if (appointmentUnitIds.includes('notSpecify')) appointmentUnitIds = map(get(this.config, 'reservation.NotSpecifyUnits'), 'id')
      }

      const resourceItemIds = [get(this.formData, 'resourceItem.id')]

      this.availableDate = []
      this.availableTimes = []
      this.availableTimesSource = []
      const payload = {
        shopId: this.shop,
        rangeStart: start.toDate(),
        rangeEnd: end.toDate(),
        appointmentUnitIds,
        resourceItemIds: isEmpty(compact(resourceItemIds)) ? null : resourceItemIds,
        appointmentServiceId:
          get(this.formData, 'service.id') || undefined,
        appointmentSubServiceId: get(this.formData, 'subService.id'),
        appointmentServiceAttachIds: map(
          get(this.formData, 'attachServices', []),
          'id',
        ),
        peopleCount: this.peopleCount,
        omitOhbookBookingIds: this.ohbotBookingId,
        setTotalBookingTime: appointmentUnitIds ? this.formData.totalTime : undefined,
        setResourceItemTotalBookingTime: isEmpty(compact(resourceItemIds)) ? undefined : this.formData.resourceTotalTime,
      }
      this.availabilityPayload = payload
      this.applyAvailabilityData(this.getCachedAvailability(payload))
    },

    updateAvailableTimes (date) {
      this.availableTimes = filter(this.availableTimesSource, (item) => {
        return dayjs(item).isSame(date, 'date')
      })

      if (this.data && this.data.start && dayjs(this.data.start).isSame(this.formData.date, 'date')) {
        const timeExists = this.availableTimes.some(time => dayjs(time).isSame(this.data.start, 'minute'))
        if (!timeExists) {
          this.availableTimes.push(this.data.start)
          this.availableTimes.sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf())
        }
      }
      // this.availableTimes = filter(res.times, (item) => {
      //   return dayjs(item).isAfter(dayjs(), 'h')
      // })
    },
    async getDate () {
      await this.$nextTick()
      const loadPickerMonth = () => {
        const picker = this.$refs.picker && this.$refs.picker.picker
        const { start, end } = picker
          ? getMonth(picker.year, picker.month + 1)
          : thisMonth()

        this.getAvailableTimes({ start: dayjs(start), end: dayjs(end) })
      }

      this.availabilityPickerPanelBinding.bind(this.$refs.picker, () => {
        this.$nextTick(loadPickerMonth)
      })
      loadPickerMonth()
    },

    dateChange (date) {
      this.formData.time = ''
      this.updateAvailableTimes(date)
    },
    updateTimeOptions () {
      this.genNums = generateNumbers(0, 600, this.shopTimeUnit)
      this.genNums.push(this.totalTime)
      this.genNums = [...new Set(this.genNums.sort((a, b) => a - b))]
      if (this.formData.type === 'humanAndResourceService') {
        this.getResourceNums = generateNumbers(0, this.formData.totalTime + this.shopTimeUnit, this.shopTimeUnit)
        this.getResourceNums.push(this.totalTime)
        this.getResourceNums = [...new Set(filter(this.getResourceNums.sort((a, b) => a - b), (item) => item <= this.formData.totalTime))]
      } else if (this.formData.type === 'resourceService') {
        this.getResourceNums = generateNumbers(0, 600, this.shopTimeUnit)
        this.getResourceNums.push(this.resourceTotalTime)
        this.getResourceNums = [...new Set(this.genNums.sort((a, b) => a - b))]
      }
    },
    totalTimeChange (newTime) {
      this.resetAvailability()
      if (this.formData.isOverTime) return
      this.formData.date = ''
      if (this.formData.resourceTotalTime > newTime) {
        this.formData.resourceTotalTime = newTime
      }
      this.updateTimeOptions()
    },
    serviceTypeChange () {
      this.resetAvailability()
      this.formData.otherService = false
      this.formData.serviceUnit = null
      this.formData.serviceDetail = null
      this.formData.service = null
      this.formData.attachServices = null
      this.formData.subService = null
      this.formData.attachServices = []
      this.formData.resourceItem = []
      this.formData.totalTime = 0
      this.formData.resourceTotalTime = 0
      this.formData.isOverTime = false
      this.formData.date = null
      this.formData.time = null
      // serviceUnit 已清空；若 serviceOptions 仍停留在原服務人員單位的子集
      // （mounted 時 findServiceUnit 的結果），切換到純設備等跨單位的服務類型
      // 會選不到項目，需重新以全店範圍拉取服務清單
      if (!this.formData.serviceUnit) this.getService()
    },

    serviceUnitChange (serviceUnit) {
      this.resetAvailability()
      this.formData.service = ''
      this.formData.attachServices = ''
      this.findServiceUnit(serviceUnit.id)
      this.updateTimeOptions()
    },
    async findServiceUnit (unitId) {
      if (unitId === '' || unitId === undefined) return
      try {
        const res = await FindServiceUnit({
          shopId: this.shop,
          id: unitId,
        })

        this.serviceOptions = sortOrder(res.AppointmentServices)
      } catch (error) {
        console.log(error)
      }
    },

    async serviceChange (data) {
      this.resetAvailability()
      if (data) await this.findService(this.formData.service.id)
      this.formData.subService = null
      this.formData.attachServices = []
      this.formData.resourceItem = []
      this.formData.totalTime = this.totalTime
      this.formData.resourceTotalTime = this.totalResourceTime
      this.classTicketUnavailable = !this.checkClassTicketStillCanUse(this.formData.service, get(this.usingClassTickets, '[0].ClassTicketRecord.ClassTicketId')) && this.checkAction('admin.appointmentReservationTicket.*')
      this.updateTimeOptions()
    },

    subServiceChange (service) {
      this.resetAvailability()
      this.formData.subService = service
      this.formData.totalTime = this.totalTime
      this.formData.resourceTotalTime = this.totalResourceTime
      this.updateTimeOptions()
      this.formData.resourceItem = []
      this.formData.attachServices = []
      this.formData.date = ''
      this.formData.time = ''
    },

    attachServiceChange () {
      this.resetAvailability()
      this.formData.date = ''
      this.formData.time = ''
      this.formData.totalTime = this.totalTime
      this.formData.resourceTotalTime = this.totalResourceTime
      this.updateTimeOptions()
    },

    genTimes () {
      return generateTimes(this.shopTimeUnit)
    },

    async updateReservationTime () {
      if (!await formUtils.checkForm(this.$refs.form)) {
        this.loading = false
        return false
      }

      const date = dayjs(this.formData.date).format('YYYY/MM/DD')
      let time = dayjs(new Date(this.formData.time)).format('HH:mm')
      if (isInvalidDate(time)) time = this.formData.time
      const start = dayjs(`${date} ${time}`).toDate()
      let totalBookingTime = this.formData.totalTime
      let resourceItemTotalBookingTime = 0

      if (get(this.formData, 'type') === 'resourceService') {
        totalBookingTime = 0
        resourceItemTotalBookingTime = this.formData.resourceTotalTime
      } else if (get(this.formData, 'type') === 'humanAndResourceService') {
        resourceItemTotalBookingTime = this.formData.resourceTotalTime
      }

      const [, err] = await UpdateReservationTimeServices({
        shopId: this.shop,
        id: this.data.id,
        start,
        resourceItemId: get(this.formData, 'resourceItem.id'),
        appointmentUnitId: get(this.formData, 'serviceUnit.id'),
        appointmentServiceId: get(this.formData, 'service.id'),
        appointmentSubServiceId: get(this.formData, 'subService.id'),
        appointmentServiceAttachIds: this.attachServicesList,
        totalBookingTime: totalBookingTime,
        resourceItemTotalBookingTime: resourceItemTotalBookingTime,
        overtime: this.formData.isOverTime,
      })

      if (err) {
        this.$message.error(err)
        this.loading = false
        return false
      }
      return true
    },

    updateInfoAndTime: debounce(async function () {
      if (this.loading) return
      this.loading = true

      if (!await this.updateReservationTime()) return
      await this.updateOrderInfo()

      clearAvailabilityQueries(this.queryClient)

      this.$message({
        message: '更新成功',
        type: 'success',
      })
      this.$emit('refresh')
      this.$emit('close')
      this.loading = false
    }, 300, { leading: false, trailing: true }),

    async findService (id) {
      const [res, err] = await FindService({
        shopId: this.shop,
        id,
      })
      if (err) {
        // CBGISSUE-243：服務項目已被軟刪除時詳情 API 會回 404（findOne 會過濾 isRemove），
        // 這時改用預約當下的歷史快照，讓欄位標記「（已刪除）」並說明原因，
        // 而不是丟出技術訊息後留一片空白
        const snapshot = get(this.data, 'AppointmentService')
        if (get(snapshot, 'id') === id) {
          this.formData.service = snapshot
          this.mainServiceData = null
          this.serviceRemoved = true
          return
        }
        this.$message.error(err)
        return
      }
      this.serviceRemoved = false
      this.formData.service = res
      this.mainServiceData = res
    },
  },
})
</script>

<style lang="postcss" scoped>
::v-deep .el-form-item {
  margin-bottom:  22px !important;
}
</style>
